// masterTracking.ts
// Centralized GearMaster page engagement tracking.
// Provides a shared hook (useSectionVisibility) and function (trackMasterEvent)
// for all /m/ page sections to push consistent GA4 + TIE events.

import { useEffect, useRef, RefObject } from 'react';
import {
  pushToDataLayer,
  pushToRevRollLayer,
  getTieCartId,
  isDoNotSellSignalActive,
} from './analytics.service';

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

// ─── Master param type ────────────────────────────────────────────────────────

export interface MasterTrackingParams {
  id: string;
  slug: string;
  brand: string;
  model: string;
  productType?: string;
}

// ─── trackMasterEvent ─────────────────────────────────────────────────────────

/**
 * Push a named engagement event for a GearMaster page to both:
 *  - GA4 dataLayer (via pushToDataLayer)
 *  - TIE/Revenue Roll dataLayer (via pushToRevRollLayer), subject to GPC
 *
 * All events include standard master product fields plus any extra payload.
 */
export function trackMasterEvent(
  eventName: string,
  master: MasterTrackingParams,
  extra?: Record<string, unknown>
): void {
  if (!isProd) return;

  try {
    // GA4 push — not subject to GPC suppression (GA4 is analytics, not retargeting)
    pushToDataLayer(eventName, {
      master_slug: master.slug,
      master_brand: master.brand,
      master_model: master.model,
      master_product_type: master.productType || '',
      master_id: master.id,
      ...extra,
    });

    // TIE/Revenue Roll push — suppressed when GPC/Do Not Sell is active
    if (!isDoNotSellSignalActive()) {
      pushToRevRollLayer('rr_master_engagement', {
        rr_cart_id: getTieCartId(),
        engagement_type: eventName,
        product_id: master.id,
        product_name: `${master.brand} ${master.model}`,
        product_brand: master.brand,
        ...extra,
      });
    }
  } catch {
    // Tracking must never throw and break the page
  }
}

// ─── useSectionVisibility ─────────────────────────────────────────────────────

/**
 * React hook that fires a `master_section_view` event once when the attached
 * element scrolls into view (30% threshold). Disconnects after first trigger
 * so the event fires exactly once per section per page load.
 *
 * Usage:
 *   const { ref } = useSectionVisibility('pricing-guide', master);
 *   <section ref={ref} ...>
 */
export function useSectionVisibility(
  sectionId: string,
  master: MasterTrackingParams
): { ref: RefObject<HTMLElement | null> } {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackMasterEvent('master_section_view', master, { section: sectionId });
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  // master.id is the stable identity — re-attach if user navigates to a different /m/ page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, master.id]);

  return { ref };
}
